import * as React from "react";
import { useEffect, useState } from "react";
import { IVizDTO, VizDTO, useGroopitApiContext } from "groopit-js";
import FormatDecimal from "../FormatDecimal";
import { Timespan, TimespanPager } from "./TimespanPager";
import useAsync from "react-use/lib/useAsync";
import { TagFilter } from "./TagFilter";
import { useInView } from "react-intersection-observer";
import { getGroupingTypes } from "../VizTypeChooser";
import {
  GroopitFilterAssignmentDTO,
  GroopitFilterDTO,
} from "groopit-js/client";

interface Leader {
  Avatar: string;
  Name: string;
  Total: number;
  Format: string;
  Id: string;
  TagTotals: [string, number][];
}

interface LeaderEntryProps {
  Avatar: string;
  Name: string;
  Total: number;
  Format: string;
  Url?: string;
}

const LeaderEntry = (props: LeaderEntryProps) => {
  const { Avatar, Name, Total, Format, Url } = props;

  const LeaderName = () => {
    return (
      <div className="flex items-center">
        <img
          title={`${Name} avatar`}
          src={Avatar}
          className="avatar-img mr-2"
        ></img>
        <div className="text-display-normal text-color-secondary">{Name}</div>
      </div>
    );
  };
  return (
    <tr>
      <td className="text-4xl text-app-orange pl-8 pr-4 text-right">
        {FormatDecimal(Total, Format)}
      </td>
      <td className="divider">
        <div></div>
      </td>
      <td className="w-full">
        {Url ? (
          // This always links to us
          // eslint-disable-next-line react/jsx-no-target-blank
          <a
            href={Url}
            target={window.self === window.top ? "_self" : "_blank"}
            className="link-groopit"
          >
            {LeaderName()}
          </a>
        ) : (
          LeaderName()
        )}
      </td>
    </tr>
  );
};

const page_size = 10;

interface LeaderboardVizProps extends Omit<IVizDTO, "title" | "description"> {
  supportsFeedDateFilters: boolean;
}

export const LeaderboardViz = (props: LeaderboardVizProps): JSX.Element => {
  const [page, setPage] = useState(1);
  const [tags, setTags] = useState<
    {
      label: string;
      active: boolean;
    }[]
  >([]);

  const onToggleTag = (tag: string) => {
    setTags((activeTags) =>
      activeTags.map((t) =>
        t.label === tag ? { ...t, active: !t.active } : t,
      ),
    );
  };
  const onSelectAll = () => {
    setTags(tags.map((t) => ({ ...t, active: true })));
  };
  const { viz } = useGroopitApiContext();

  const data = useAsync(async () => {
    const json = await viz.getDataPOST(new VizDTO({ title: "", ...props }));
    const leaders = json as Leader[];
    if (props.filterId) {
      // filter by tags enabled, compute the total set of tags in the
      // returned data
      const tags = Array.from(
        new Set(leaders.flatMap((l) => l.TagTotals.map((t) => t[0]))),
      )
        .sort()
        .map((t) => ({ label: t, active: true }));
      setTags(tags);
    } else {
      setTags([]);
    }
    return leaders;
  }, [props]);

  if (data.error) {
    return <div>Error: {data.error.message}</div>;
  }
  if (data.loading || !data.value) {
    return <div>Loading...</div>;
  }

  //
  // If there is tag filtering active, then we need to manually compute and sort
  // the leaderboard based on the active tags. Otherwise, we can just use the
  // data returned from the server directly.
  //
  const leaders = props.filterId
    ? data.value
        .map((l) => ({
          ...l,
          Total: tags
            .filter((t) => t.active)
            .map((t) => l.TagTotals?.find((x) => x[0] === t.label)?.[1] ?? 0)
            .reduce((acc, t) => acc + t, 0),
        }))
        .filter((l) => l.Total > 0)
        .sort((a, b) => b.Total - a.Total)
    : data.value;

  const partial = leaders.length > page * page_size;

  //
  // Calculate the right URL to take the user to a filtered view of
  // the groopits. This includes the creator, the assignment (if any)
  // the tags (if any), and the date range (if any and the client supports it)
  //
  const leaderUrl = (leader: Leader) => {
    //
    // If tag filtering is enabled, don't generate a URL. There are a couple
    // problems with this.
    // 1. Feed tag filtering filters on AND, not OR. And the leaderboard by
    //    tag filters on OR, not AND. So we can't use the tag filtering on
    //    the leaderboard to show a similarly filtered feed view.
    // 2. A filtered leaderboard excludes all entries with no tags at all.
    //    A filtered feed view includes all entries with no tags. So it generates
    //    a weird disconnect between the count in the leaderboard and the count
    //    in the feed view.
    //
    if (props.filterId) {
      return undefined;
    }

    //
    // If end date filtering is enabled and we're on a client that doesn't support
    // date filering in the feed, don't generate a URL.
    //
    if (
      !props.supportsFeedDateFilters &&
      props.endDate &&
      props.endDate < new Date()
    ) {
      return undefined;
    }

    const filter = new GroopitFilterDTO();
    if (props.assignmentId) {
      filter.assignments = [
        new GroopitFilterAssignmentDTO({
          id: props.assignmentId,
          creators: [leader.Id],
          startDate: props.startDate,
          endDate: props.endDate,
        }),
      ];
    } else {
      filter.creators = [leader.Id];
      filter.startDate = props.startDate;
      filter.endDate = props.endDate;
    }

    return `/Groop/Groopits/${props.groopId}?filter=${encodeURIComponent(
      JSON.stringify(filter),
    )}`;
  };

  return (
    <div className="leaderboard-viz">
      {page > 1 ? (
        <div>
          Top {(page - 1) * page_size + 1} -{" "}
          {Math.min(leaders.length, page * page_size)}
        </div>
      ) : leaders.length ? (
        <div>Top {Math.min(leaders.length, page_size)}</div>
      ) : (
        <></>
      )}
      {leaders.length ? (
        <table>
          <tbody>
            {leaders
              .slice((page - 1) * page_size, page * page_size)
              .map((tag, i) => (
                <LeaderEntry
                  key={i}
                  {...tag}
                  Url={leaderUrl(tag)}
                ></LeaderEntry>
              ))}
          </tbody>
        </table>
      ) : (
        <div className="text-center">no data...</div>
      )}
      <div className="text-color-primary text-display-subtitle">
        {page > 1 && (
          <button className="pull-left" onClick={() => setPage(page - 1)}>
            <span
              className="glyphicon glyphicon-chevron-left"
              aria-hidden="true"
            ></span>
            PREV
          </button>
        )}
        {partial && (
          <button className="pull-right" onClick={() => setPage(page + 1)}>
            NEXT
            <span
              className="glyphicon glyphicon-chevron-right"
              aria-hidden="true"
            ></span>
          </button>
        )}
      </div>
      {tags.length > 0 && (
        <TagFilter
          tags={tags}
          onToggleTag={onToggleTag}
          onSelectAll={onSelectAll}
        ></TagFilter>
      )}
    </div>
  );
};

export const LeaderboardVizWrapper = (
  props: LeaderboardVizProps,
): JSX.Element => {
  const [timespan, setTimespan] = useState(
    new Timespan(props.endDate, props.grouping, props.startDate, props.endDate),
  );
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: "100px 0px",
  });
  useEffect(() => {
    if (
      props.startDate != timespan.startDate ||
      props.endDate != timespan.endDate ||
      props.grouping != timespan.grouping
    ) {
      setTimespan(
        new Timespan(
          timespan.date,
          props.grouping,
          props.startDate,
          props.endDate,
        ),
      );
    }
  }, [props.startDate, props.endDate, props.grouping]);

  return (
    <div ref={ref}>
      <TimespanPager
        timespan={timespan}
        on_change={setTimespan}
        groupings={getGroupingTypes(props.vizType, props.operation)}
      />
      {inView && (
        <LeaderboardViz
          {...props}
          grouping={timespan.grouping}
          startDate={timespan.start}
          endDate={timespan.end}
        ></LeaderboardViz>
      )}
    </div>
  );
};
